import tools from '@/api/assistant/tools'
import { buildWorkflowAssistantPrompt, buildFunctionCallResponsePrompt } from '@/ai/prompts'
import { fetchJsonResponse, streamTextResponse } from '@/ai/api'
import { friendlyAiMessage } from '@/ai/api/aiErrors'

export const generateWorkflowAssistant = async ({
  workflow,
  messagesHistory = [],
  prompt,
  abortController,
  currentUIState,
  currentTurnOutputs = [],
  collaboratorDrift = false,
  onRetry
}) => {
  if (!workflow) {
    return null
  }

  const messages = buildWorkflowAssistantPrompt({
    workflow,
    messagesHistory,
    prompt,
    currentUIState,
    currentTurnOutputs,
    collaboratorDrift
  })

  try {
    const response = await fetchJsonResponse({
      messages,
      aiVersion: workflow.aiVersion || 5,
      tools,
      signal: abortController?.signal,
      promptCacheKey: workflow.id,
      onRetry
    })

    return response || null
  } catch (error) {
    // If the user cancels, treat it as a normal no-op.
    if (abortController?.signal?.aborted || error?.name === 'AbortError') {
      return null
    }

    console.error('Workflow assistant request failed:', error)

    return {
      error: {
        name: error?.name || 'Error',
        message: friendlyAiMessage(error)
      }
    }
  }
}

export const generateFunctionCallResponse = async ({
  workflow,
  prompt,
  updatesSummary = [],
  incomplete = false,
  onDelta,
  abortController
}) => {
  if (!workflow || !updatesSummary.length) {
    return null
  }

  const messages = buildFunctionCallResponsePrompt({
    prompt,
    updatesSummary,
    incomplete
  })

  let fullText = ''
  try {
    await streamTextResponse({
      messages,
      aiVersion: workflow.aiVersion || 5,
      tools: [],
      signal: abortController?.signal,
      onChunkReceived: (chunk) => {
        fullText += chunk

        if (typeof onDelta === 'function') {
          onDelta(fullText)
        }
      }
    })

    return fullText || null
  } catch (error) {
    console.error('Error generating function call response:', error)
    return null
  }
}
